import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/context/AuthContext';
import { MarketplaceProvider } from '@/components/site/marketplace-context';
import MainLayout from '@/components/layout/MainLayout';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import IndexPage from '@/pages/Index';
import ListingsPage from '@/pages/Listings';
import ListingDetailPage from '@/pages/ListingDetail';
import AuthPage from '@/pages/Auth';
import NotFoundPage from '@/pages/NotFound';
import PostAdPage from '@/pages/PostAd';
import ProfilePage from '@/pages/Profile';
import SettingsPage from '@/pages/Settings';
import MessagesPage from '@/pages/Messages';
import MyListingsPage from '@/pages/MyListings';
import AdminPage from '@/pages/Admin';
import SellerDashboardPage from '@/pages/SellerDashboard';
import FindFriendsPage from '@/pages/FindFriends';
import TermsOfServicePage from '@/pages/legal/TermsOfService';
import PrivacyPolicyPage from '@/pages/legal/PrivacyPolicy';
import CookiePolicyPage from '@/pages/legal/CookiePolicy';
import BlogPage from '@/pages/Blog';
import BlogPostPage from '@/pages/BlogPost';
import ContactPage from '@/pages/Contact';
import AboutPage from '@/pages/About';
import HelpCenterPage from '@/pages/HelpCenter';
import SafetyTipsPage from '@/pages/SafetyTips';
import SuccessStoriesPage from '@/pages/SuccessStories';
import AnalyticsDashboardPage from '@/pages/AnalyticsDashboard';
import SitemapPage from '@/pages/Sitemap';
import BuyUsedCarsPage from '@/pages/BuyUsedCars';
import BuyUsedMobilesPage from '@/pages/BuyUsedMobiles';
import BuyUsedFurniturePage from '@/pages/BuyUsedFurniture';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <MarketplaceProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ErrorBoundary>
              <Routes>
                <Route path="/" element={<MainLayout />}>
                  <Route index element={<IndexPage />} />
                  <Route path="listings" element={<ListingsPage />} />
                  <Route path="listings/:id" element={<ListingDetailPage />} />
                  <Route path="post" element={<PostAdPage />} />
                  <Route path="login" element={<AuthPage />} />
                  <Route path="auth" element={<AuthPage />} />
                  {/* Guide Pages - Static SEO Content */}
                  <Route path="buy-used-cars" element={<BuyUsedCarsPage />} />
                  <Route path="buy-used-mobiles" element={<BuyUsedMobilesPage />} />
                  <Route path="buy-used-furniture" element={<BuyUsedFurniturePage />} />
                  {/* Blog Pages */}
                  <Route path="blog" element={<BlogPage />} />
                  <Route path="blog/:slug" element={<BlogPostPage />} />
                  {/* Contact Page */}
                  <Route path="contact" element={<ContactPage />} />
                  {/* About Page */}
                  <Route path="about" element={<AboutPage />} />
                  {/* Support Pages */}
                  <Route path="help" element={<HelpCenterPage />} />
                  <Route path="safety" element={<SafetyTipsPage />} />
                  <Route path="success-stories" element={<SuccessStoriesPage />} />
                  <Route path="sitemap" element={<SitemapPage />} />
                  {/* Legal Pages */}
                  <Route path="terms" element={<TermsOfServicePage />} />
                  <Route path="privacy" element={<PrivacyPolicyPage />} />
                  <Route path="cookies" element={<CookiePolicyPage />} />
                  <Route path="not-found" element={<NotFoundPage />} />
                  <Route path="*" element={<Navigate to="/not-found" replace />} />

                  {/* Friends (public) */}
                  <Route path="friends" element={<FindFriendsPage />} />

                  {/* Protected Routes - require authentication */}
                  <Route element={<ProtectedRoute />}>
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="messages" element={<MessagesPage />} />
                    <Route path="my-listings" element={<MyListingsPage />} />
                    <Route path="seller-dashboard" element={<SellerDashboardPage />} />
                  </Route>

                  {/* Admin-only route */}
                  <Route element={<ProtectedRoute requiredRole="ADMIN" />}>
                    <Route path="admin" element={<AdminPage />} />
                    <Route path="admin/analytics" element={<AnalyticsDashboardPage />} />
                  </Route>
                </Route>
              </Routes>
            </ErrorBoundary>
          </Router>
        </MarketplaceProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;